import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";

interface PromptItem {
  key: keyof import("gen/clients").AiPrompts;
  name: string;
  description: string;
  value: string;
}

interface Props {
  prompt: PromptItem;
  onClose: () => void;
  onSave: (updatedValue: string) => Promise<void>;
  isLoading: boolean;
}

enum FieldName {
  promptText = "promptText"
}

interface FormValues {
  [FieldName.promptText]: string;
}

const validationSchema = Yup.object().shape({
  [FieldName.promptText]: Yup.string().required("Prompt text is required")
});

const EditAiPromptDialog: React.FC<Props> = ({ prompt, onClose, onSave, isLoading }) => {
  const initialValues: FormValues = {
    [FieldName.promptText]: prompt.value
  };

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await onSave(values[FieldName.promptText]);
    },
    [onSave]
  );

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit AI Prompt</DialogTitle>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <DialogContent>
              <Stack spacing={2}>
                <Typography variant="h6" gutterBottom>
                  {prompt.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {prompt.description}
                </Typography>
                <TextInputField
                  name={FieldName.promptText}
                  label="Prompt Text"
                  helperText="Enter the AI prompt instructions. This text will be used to guide AI processing."
                  variant="outlined"
                  multiline
                  rows={12}
                  required
                  fullWidth
                  autoFocus
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting || isLoading}>
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                isLoading={isSubmitting || isLoading}
                disabled={!isValid || !dirty}
              >
                Save
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { EditAiPromptDialog };
